import * as MenuPrimitive from '@radix-ui/react-dropdown-menu'
import type { PropsWithChildren, ReactNode } from 'react'
import { forwardRef } from 'react'
import tw, { styled, css } from 'twin.macro'

import type { DropdownMenuItemProps, DropdownContentProps } from './types'

export const DropdownMenuOverlay = tw.div`
  fixed
  inset-0
  bg-default-muted
  bg-opacity-50
  md:hidden
`

const elevationStyleMap = {
  none: '',
  normal: tw`shadow`,
  sm: tw`shadow-sm`,
  md: tw`shadow-md`,
  lg: tw`shadow-lg`,
  xl: tw`shadow-xl`,
  '2xl': tw`shadow-2xl`,
} as const

const defaultBorder = tw`border border-default-emphasis border-solid`

// This is just a rename property of DropdownContentProps
type Props = Exclude<DropdownContentProps, 'elevation'> & {
  // The $ in elevation is because this is a transient prop of styled-components
  // https://styled-components.com/docs/api#transient-props
  $elevation: DropdownContentProps['elevation']
}

export const DropdownMenuContent = styled(MenuPrimitive.Content)(
  ({ $elevation = 'sm' }: Props) => [
    // If there's no sufficient elevation, add a border so it pops out more
    ['none', 'normal', 'sm'].includes($elevation) && defaultBorder,
    elevationStyleMap[$elevation] ?? elevationStyleMap.sm,
    css`
      ${tw`
      w-full
      p-2
      bg-default-subdued
      z-10
      rounded
      font-brand
      text-left
    `}
      /** uncomment once we can stylize the mobile version */
  /** Needs to be max-width because we need to override the width */
  @media (max-width: 767px) {
        /** Overwrite width so it fills the window */
        ${tw`
      // w-full!
      // fixed
      // inset-auto
      // bottom-0
      // left-0
      // right-0
    `}
      }
    `,
  ],
)

const Slot = styled.div(({ $hasSubtitle }: { $hasSubtitle: boolean }) => [
  $hasSubtitle && tw`mt-1`,
])

const LeftSlot = styled(Slot)(() => [tw`mr-2`])
const RightSlot = styled(Slot)(() => [tw`ml-2`])

const ItemContent = tw.div`flex-1`

const ItemRow = styled(MenuPrimitive.Item)(
  ({
    $startSlot,
    $endSlot,
    $hasSubtitle,
    disabled,
  }: DropdownMenuItemProps & {
    $startSlot?: ReactNode
    $endSlot?: ReactNode
    $hasSubtitle?: boolean
  }) => [
    tw`
      flex
      justify-between
      outline-none
      text-default-emphasis
      font-brand text-sm-brand leading-normal font-medium
      py-3
      rounded
    `,
    $hasSubtitle ? tw`items-start` : tw`items-center`,
    $startSlot ? tw`pl-2` : tw`pl-4`,
    $endSlot ? tw`pr-2` : tw`pr-4`,
    disabled
      ? tw`cursor-not-allowed opacity-50`
      : tw`
        cursor-pointer
        hover:text-brand-neutral
        hover:bg-default-muted
        focus:bg-default-muted
      `,
    !disabled &&
      css`
        ${Slot} {
          ${tw`text-default-muted`}
        }

        &:hover ${Slot} {
          ${tw`text-brand-neutral`}
        }
      `,
  ],
)

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  PropsWithChildren<DropdownMenuItemProps>
>(function DropdownItem(
  { children, hasSubtitle = false, startSlot, endSlot, ...rest },
  ref,
) {
  return (
    <ItemRow
      ref={ref}
      $startSlot={startSlot}
      $endSlot={endSlot}
      $hasSubtitle={hasSubtitle}
      {...rest}
    >
      {startSlot && <LeftSlot $hasSubtitle={hasSubtitle}>{startSlot}</LeftSlot>}
      <ItemContent>{children}</ItemContent>
      {endSlot && <RightSlot $hasSubtitle={hasSubtitle}>{endSlot}</RightSlot>}
    </ItemRow>
  )
})

export const DropdownMenuTitle = tw.div``

export const DropdownMenuSubtitle = tw.div`
  text-default-muted
  text-sm-brand
  md:text-xs-brand
`
